import { api, decodeVaultBlobResponse } from '../api/client';
import { guestApi, type GuestSession } from '../api/guest';
import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import {
  deriveGuestLoginVerifierFromPassphrase,
  encodeLoginVerifierBase64,
} from '../crypto/guestLoginVerifier';
import { decodeBase64, decodeBase64Url, encodeBase64 } from '../crypto/primitives';
import { enforcePassphraseMaxAge } from '../crypto/passphraseAge';
import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';
import { defaultPolicyForTarget } from '../crypto/passwordPolicy';
import {
  setupVault,
  unlockVaultWithPassphrase,
  setStoredVaultVersion,
} from '../crypto/vault';
import {
  storeVaultSession,
  hasVaultSession,
  loadVaultSession,
} from '../crypto/vaultSession';
import { wrapKey } from '../crypto/wrappedKey';
import { decryptSedrGuestEnvelope } from '../crypto/sedrGuestInvite';
import { resolveSedrGuestFragment } from '../crypto/sedrGuestFragment';
import { deriveProofKeys, signGateChallenge } from '../crypto/infoShareFragment';
import {
  listPendingGuestInviteSeeds,
  removePendingGuestInviteSeed,
  upsertPendingGuestInviteSeed,
  type PendingGuestInviteSeed,
} from '../crypto/guestInviteStash';
import { x25519 } from '@noble/curves/ed25519';
import { GuestSettingsPage } from './GuestSettingsPage';
import { isGuestSettingsPath } from '../app/navigationState';
import { createHelpTip } from '../help/helpTips';
import { setGuestShellName } from '../ui/guestShellName';
import { attachPassphraseWhitespaceWarnings } from '../ui/passphraseWhitespaceWarning';
import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from '../ui/passwordPolicyUi';
import { renderSVG } from 'uqr';
import { GuestSedrModulePage } from './GuestSedrModulePage';

function panel(html: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'center-panel guest-portal';
  el.innerHTML = html;
  applyI18n(el);
  return el;
}

function setFormBusy(
  form: HTMLFormElement,
  busy: boolean,
  options?: { button?: HTMLButtonElement | null; status?: HTMLElement | null; statusKey?: string },
): void {
  const button =
    options?.button ?? (form.querySelector('button[type="submit"]') as HTMLButtonElement | null);
  form.querySelectorAll('input, button, select, textarea').forEach((el) => {
    (el as HTMLInputElement).disabled = busy;
  });
  if (button) {
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
  if (options?.status) {
    if (busy && options.statusKey) {
      options.status.hidden = false;
      options.status.className = 'muted guest-form-status';
      options.status.textContent = t(options.statusKey);
    } else if (!busy) {
      options.status.hidden = true;
      options.status.textContent = '';
      options.status.className = 'error-text';
    }
  }
}

export function GuestPortalPage(): HTMLElement {
  const path = window.location.pathname.replace(/\/$/, '') || '/guest';
  const inviteMatch = path.match(/^\/guest\/invite\/([^/]+)$/);
  if (inviteMatch) {
    return GuestInviteClaimPage(inviteMatch[1]);
  }
  if (isGuestSettingsPath(path)) {
    return GuestSettingsPage();
  }
  const roomMatch = path.match(/^\/guest\/rooms\/([^/]+)/);
  if (roomMatch) {
    return GuestRoomDetailPage(decodeURIComponent(roomMatch[1]));
  }
  return GuestLoginOrHomePage();
}

function GuestLoginOrHomePage(): HTMLElement {
  const root = panel(`<p data-i18n="status.loading"></p>`);
  void (async () => {
    try {
      const session = await guestApi.session();
      if (!session.vaultSetupComplete) {
        root.replaceWith(GuestSignupForm({
          email: session.email,
          displayName: session.displayName,
          organization: session.organization ?? undefined,
        }));
        return;
      }
      if (session.totpEnrolled && !session.totpVerified) {
        root.replaceWith(GuestTotpChallengeForm(session));
        return;
      }
      if (!hasVaultSession()) {
        root.replaceWith(GuestUnlockForm(session));
        return;
      }
      setGuestShellName(session.displayName, session.organization);
      root.replaceWith(GuestSedrModulePage());
    } catch {
      const seeds = listPendingGuestInviteSeeds();
      const pending = seeds.length > 0 ? seeds[seeds.length - 1] : null;
      if (pending?.email) {
        root.replaceWith(
          GuestSignupForm({
            email: pending.email,
            displayName: pending.displayName,
            organization: pending.organization,
            showLoginLink: true,
          }),
        );
        return;
      }
      root.replaceWith(GuestLoginForm(pending?.email ?? undefined));
    }
  })();
  return root;
}

function GuestLoginForm(prefillEmail?: string): HTMLElement {
  const el = panel(`
    <h1 data-i18n="guest.login.title"></h1>
    <p class="muted" data-i18n="guest.login.subtitle"></p>
    <form class="form-grid">
      <label>
        <span data-i18n="guest.login.email"></span>
        <input type="email" name="email" required autocomplete="username" />
      </label>
      <label>
        <span data-i18n="guest.login.passphrase"></span>
        <input type="password" name="passphrase" required minlength="12" autocomplete="current-password" />
      </label>
      <p class="error-text" hidden></p>
      <button type="submit" class="button primary" data-i18n="guest.login.button"></button>
    </form>
    <p class="muted"><a href="/share" data-i18n="guest.login.quickShareLink"></a></p>
  `);
  const emailInput = el.querySelector('input[name="email"]') as HTMLInputElement;
  if (prefillEmail) {
    emailInput.value = prefillEmail;
  }
  const form = el.querySelector('form')!;
  attachPassphraseWhitespaceWarnings(
    form.querySelector('input[name="passphrase"]') as HTMLInputElement,
  );
  const error = el.querySelector('.error-text') as HTMLElement;
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const data = new FormData(form);
      const email = String(data.get('email') ?? '').trim();
      const passphrase = String(data.get('passphrase') ?? '');
      setFormBusy(form, true, {
        button: submit,
        status: error,
        statusKey: 'guest.login.working',
      });
      try {
        const challenge = await guestApi.loginChallenge(email);
        const vaultSalt = decodeBase64(challenge.vaultSalt);
        const { loginVerifier } = await deriveGuestLoginVerifierFromPassphrase(passphrase, vaultSalt);
        const session = await guestApi.login(email, encodeLoginVerifierBase64(loginVerifier));
        const blob = decodeVaultBlobResponse(await api.getVaultBlob());
        const unlocked = await unlockVaultWithPassphrase(
          passphrase,
          blob.vaultSalt,
          blob.vkWrapPassphrase,
          blob.vaultBlob,
          blob.vaultVersion,
        );
        storeVaultSession(unlocked.vaultKey, unlocked.userPrivateKey);
        setStoredVaultVersion(session.userId, Number(session.vaultVersion));
        const expired = await enforcePassphraseMaxAge({
          passphraseSetAt: blob.passphraseSetAt,
          target: 'guestVaultPassphrase',
          guest: true,
        });
        if (expired) {
          setFormBusy(form, false, { button: submit, status: error });
          return;
        }
        try {
          await claimAllPendingInvites(unlocked.userPrivateKey);
        } catch (claimCause) {
          console.error('claim after login failed', claimCause);
        }
        const refreshed = await guestApi.session();
        await continueAfterAuth(refreshed, el);
      } catch (cause) {
        setFormBusy(form, false, { button: submit, status: error });
        error.hidden = false;
        error.className = 'error-text';
        error.textContent = t('guest.login.error');
        console.error(cause);
      }
    })();
  });
  return el;
}

function GuestUnlockForm(session: GuestSession): HTMLElement {
  setGuestShellName(session.displayName, session.organization);
  const el = panel(`
    <h1 data-i18n="guest.unlock.title"></h1>
    <p class="muted">${escapeHtml(session.email ?? '')}</p>
    <form class="form-grid">
      <label>
        <span data-i18n="guest.login.passphrase"></span>
        <input type="password" name="passphrase" required minlength="12" autocomplete="current-password" />
      </label>
      <p class="error-text" hidden></p>
      <button type="submit" class="button primary" data-i18n="guest.unlock.button"></button>
    </form>
  `);
  const form = el.querySelector('form')!;
  attachPassphraseWhitespaceWarnings(
    form.querySelector('input[name="passphrase"]') as HTMLInputElement,
  );
  const error = el.querySelector('.error-text') as HTMLElement;
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      setFormBusy(form, true, {
        button: submit,
        status: error,
        statusKey: 'guest.unlock.working',
      });
      try {
        const passphrase = String(new FormData(form).get('passphrase') ?? '');
        const blob = decodeVaultBlobResponse(await api.getVaultBlob());
        const unlocked = await unlockVaultWithPassphrase(
          passphrase,
          blob.vaultSalt,
          blob.vkWrapPassphrase,
          blob.vaultBlob,
          blob.vaultVersion,
        );
        storeVaultSession(unlocked.vaultKey, unlocked.userPrivateKey);
        const expired = await enforcePassphraseMaxAge({
          passphraseSetAt: blob.passphraseSetAt,
          target: 'guestVaultPassphrase',
          guest: true,
        });
        if (expired) {
          setFormBusy(form, false, { button: submit, status: error });
          return;
        }
      } catch (cause) {
        setFormBusy(form, false, { button: submit, status: error });
        error.hidden = false;
        error.className = 'error-text';
        error.textContent = t('guest.login.error');
        console.error(cause);
        return;
      }

      try {
        const vault = loadVaultSession();
        if (vault) {
          await claimAllPendingInvites(vault.userPrivateKey);
        }
      } catch (claimCause) {
        console.error('claim after unlock failed', claimCause);
      }
      try {
        const refreshed = await guestApi.session();
        await continueAfterAuth(refreshed, el);
      } catch {
        window.location.assign('/guest/');
      }
    })();
  });
  return el;
}

function GuestSignupForm(prefill?: {
  email?: string | null;
  displayName?: string;
  organization?: string;
  showLoginLink?: boolean;
}): HTMLElement {
  const email = (prefill?.email ?? '').trim();
  const displayName = (prefill?.displayName ?? '').trim();
  const organization = (prefill?.organization ?? '').trim();
  const loginUrl = `${window.location.origin}/guest/`;
  const orgRow = organization
    ? `<div class="guest-signup-identity__row">
        <dt data-i18n="guest.signup.organization"></dt>
        <dd>${escapeHtml(organization)}</dd>
      </div>`
    : '';
  const loginLink = prefill?.showLoginLink
    ? `<p class="muted"><a href="/guest/" data-i18n="guest.signup.haveAccount"></a></p>`
    : '';
  const el = panel(`
    <h1 data-i18n="guest.signup.title"></h1>
    <p class="muted">${t('guest.signup.subtitle', { loginUrl })}</p>
    <section class="guest-signup-identity" aria-labelledby="guest-signup-identity-heading">
      <h2 id="guest-signup-identity-heading" class="guest-signup-identity__title" data-i18n="guest.signup.identityTitle"></h2>
      <p class="muted guest-signup-identity__note" data-i18n="guest.signup.identityNote"></p>
      <dl class="guest-signup-identity__list">
        <div class="guest-signup-identity__row">
          <dt data-i18n="guest.login.email"></dt>
          <dd>${escapeHtml(email)}</dd>
        </div>
        <div class="guest-signup-identity__row">
          <dt data-i18n="guest.signup.displayName"></dt>
          <dd>${escapeHtml(displayName)}</dd>
        </div>
        ${orgRow}
      </dl>
    </section>
    ${loginLink}
    <form class="form-grid guest-signup-form">
      <label>
        <span data-i18n="guest.signup.passphrase"></span>
        <input type="password" name="passphrase" required minlength="12" autocomplete="new-password" />
      </label>
      <label>
        <span data-i18n="guest.signup.passphraseConfirm"></span>
        <input type="password" name="passphraseConfirm" required minlength="12" autocomplete="new-password" />
      </label>
      <p class="muted" id="guest-signup-policy-hint" hidden></p>
      <p class="error-text" hidden></p>
      <button type="submit" class="button primary" data-i18n="guest.signup.button"></button>
    </form>
  `);
  const form = el.querySelector('form')!;
  const passphraseInput = form.querySelector('input[name="passphrase"]') as HTMLInputElement;
  const confirmInput = form.querySelector('input[name="passphraseConfirm"]') as HTMLInputElement;
  const policyHint = el.querySelector('#guest-signup-policy-hint') as HTMLElement;
  let signupPolicy = defaultPolicyForTarget('guestVaultPassphrase');
  void resolvePasswordPolicy('guestVaultPassphrase').then((policy) => {
    signupPolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [passphraseInput, confirmInput],
      hint: policyHint,
      email,
    });
  });
  attachPassphraseWhitespaceWarnings(passphraseInput, confirmInput);
  const error = el.querySelector('.error-text') as HTMLElement;
  const submit = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const data = new FormData(form);
      const passphrase = String(data.get('passphrase') ?? '');
      const confirm = String(data.get('passphraseConfirm') ?? '');
      if (!email || !displayName) {
        error.hidden = false;
        error.textContent = t('guest.signup.error');
        return;
      }
      if (passphrase !== confirm) {
        error.hidden = false;
        error.textContent = t('guest.signup.passphraseMismatch');
        return;
      }
      const policyMessage = validatePasswordForUi(passphrase, signupPolicy, { email });
      if (policyMessage) {
        error.hidden = false;
        error.textContent = policyMessage;
        return;
      }
      setFormBusy(form, true, {
        button: submit,
        status: error,
        statusKey: 'guest.signup.working',
      });
      try {
        const vault = await setupVault(passphrase, signupPolicy, { email });
        const { loginVerifier } = await deriveGuestLoginVerifierFromPassphrase(
          passphrase,
          vault.vaultSalt,
        );
        const session = await guestApi.signup({
          email,
          displayName,
          organization: organization || null,
          loginVerifier: encodeLoginVerifierBase64(loginVerifier),
          vaultBlob: vault.vaultBlob,
          vaultSalt: vault.vaultSalt,
          vkWrapPassphrase: vault.vkWrapPassphrase,
          userPublicKey: vault.userPublicKey,
          vaultVersion: 1,
        });
        storeVaultSession(vault.vaultKey, vault.userPrivateKey);
        setStoredVaultVersion(session.userId, 1);
        setFormBusy(form, true, {
          button: submit,
          status: error,
          statusKey: 'guest.signup.claiming',
        });
        let requireTotp = false;
        try {
          await claimAllPendingInvites(vault.userPrivateKey);
          const rooms = await guestApi.listRooms();
          requireTotp = rooms.some((r) => r.requireGuestTotp);
        } catch (claimCause) {
          console.error(claimCause);
        }
        if (requireTotp && !session.totpEnrolled) {
          el.replaceWith(GuestTotpEnrollForm({ nextHref: '/guest/' }));
          return;
        }
        window.location.assign('/guest/');
      } catch (cause) {
        setFormBusy(form, false, { button: submit, status: error });
        error.hidden = false;
        error.className = 'error-text';
        const code = (cause as { code?: string } | null)?.code;
        error.textContent =
          passwordPolicyErrorFromCause(cause, signupPolicy)
          ?? (code === 'claim_failed'
            ? t('guest.signup.claimError', { loginUrl: `${window.location.origin}/guest/` })
            : t('guest.signup.error'));
        console.error(cause);
      }
    })();
  });
  return el;
}

function GuestTotpChallengeForm(
  session: GuestSession,
  opts?: { nextHref?: string },
): HTMLElement {
  const nextHref = opts?.nextHref ?? '/guest/';
  setGuestShellName(session.displayName, session.organization);
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr module-page--guest-portal';
  page.innerHTML = `
    <div class="module-section" id="sedr-guest-gate">
      <h2 data-i18n="sedr.guest.title"></h2>
      <p class="muted" data-i18n="sedr.guest.subtitle"></p>
      <form id="sedr-guest-totp-challenge" class="form-grid">
        <p class="muted" data-i18n="sedr.guest.totpChallengeHint"></p>
        <label>
          <span data-i18n="sedr.guest.totpCode"></span>
          <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
        </label>
        <button type="submit" class="button primary" data-i18n="sedr.guest.enter"></button>
      </form>
      <p class="error-text" id="sedr-guest-error" hidden></p>
    </div>
  `;
  applyI18n(page);
  page.querySelector('#sedr-guest-gate h2')?.append(createHelpTip('guest-sedr-dashboard'));

  const form = page.querySelector('#sedr-guest-totp-challenge') as HTMLFormElement;
  const error = page.querySelector('#sedr-guest-error') as HTMLElement;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const code = String(new FormData(form).get('totpCode') ?? '');
      try {
        await guestApi.totpChallenge(code);
        window.location.assign(nextHref);
      } catch (cause) {
        console.error(cause);
        error.hidden = false;
        error.textContent = t('sedr.guest.incorrectTotp');
      }
    })();
  });
  return page;
}

function GuestTotpEnrollForm(opts?: { nextHref?: string }): HTMLElement {
  const nextHref = opts?.nextHref ?? '/guest/';
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr module-page--guest-portal';
  page.innerHTML = `
    <div class="module-section" id="sedr-guest-gate">
      <h2 data-i18n="sedr.guest.title"></h2>
      <p class="muted" data-i18n="sedr.guest.subtitle"></p>
      <div id="sedr-guest-totp-enroll" class="form-grid">
        <p class="muted" data-i18n="sedr.guest.totpEnrollHint"></p>
        <div id="sedr-guest-totp-qr" class="sedr-guest-totp-qr" aria-hidden="true"></div>
        <label>
          <span data-i18n="sedr.guest.totpSecret"></span>
          <input type="text" id="sedr-guest-totp-secret" readonly autocomplete="off" />
        </label>
        <form id="sedr-guest-totp-enroll-form" class="form-grid">
          <label>
            <span data-i18n="sedr.guest.totpCode"></span>
            <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
          </label>
          <button type="submit" class="button primary" data-i18n="sedr.guest.totpConfirm"></button>
        </form>
      </div>
      <p class="error-text" id="sedr-guest-error" hidden></p>
    </div>
  `;
  applyI18n(page);
  page.querySelector('#sedr-guest-gate h2')?.append(createHelpTip('guest-sedr-dashboard'));
  void guestApi.session()
    .then((s) => setGuestShellName(s.displayName, s.organization))
    .catch(() => undefined);

  const form = page.querySelector('#sedr-guest-totp-enroll-form') as HTMLFormElement;
  const error = page.querySelector('#sedr-guest-error') as HTMLElement;
  const secretInput = page.querySelector('#sedr-guest-totp-secret') as HTMLInputElement;
  const qrHost = page.querySelector('#sedr-guest-totp-qr') as HTMLElement;
  let pendingToken: string | null = null;
  void (async () => {
    try {
      const start = await guestApi.totpEnrollStart();
      pendingToken = start.pendingToken;
      secretInput.value = start.secret ?? '';
      qrHost.innerHTML = start.otpauthUri ? renderSVG(start.otpauthUri, { ecc: 'M', pixelSize: 4 }) : '';
    } catch (cause) {
      console.error(cause);
      error.hidden = false;
      error.textContent = t('status.error');
    }
  })();
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      if (!pendingToken) return;
      error.hidden = true;
      const code = String(new FormData(form).get('totpCode') ?? '');
      try {
        await guestApi.totpEnrollConfirm(pendingToken, code);
        window.location.assign(nextHref);
      } catch (cause) {
        console.error(cause);
        error.hidden = false;
        error.textContent = t('sedr.guest.incorrectTotp');
      }
    })();
  });
  return page;
}

async function continueAfterAuth(session: GuestSession, host: HTMLElement): Promise<void> {
  if (session.totpEnrolled && !session.totpVerified) {
    host.replaceWith(GuestTotpChallengeForm(session));
    return;
  }
  window.location.assign('/guest/');
}

function GuestRoomDetailPage(roomId: string): HTMLElement {
  const root = panel(`<p data-i18n="status.loading"></p>`);
  const roomHref = `/guest/rooms/${encodeURIComponent(roomId)}`;
  void (async () => {
    try {
      const session = await guestApi.session();
      setGuestShellName(session.displayName, session.organization);

      if (!hasVaultSession() || !loadVaultSession()) {
        root.replaceWith(GuestUnlockForm(session));
        return;
      }

      const room = await guestApi.getRoom(roomId);
      if (room.requireGuestTotp && !session.totpEnrolled) {
        root.replaceWith(GuestTotpEnrollForm({ nextHref: roomHref }));
        return;
      }
      if (session.totpEnrolled && !session.totpVerified) {
        root.replaceWith(GuestTotpChallengeForm(session, { nextHref: roomHref }));
        return;
      }

      root.replaceWith(GuestSedrModulePage());
    } catch (cause) {
      console.error(cause);
      root.replaceWith(panel(`<p class="error-text" data-i18n="status.error"></p>
        <p><a href="/guest/" data-i18n="guest.settings.back"></a></p>`));
    }
  })();
  return root;
}

function GuestInviteClaimPage(inviteId: string): HTMLElement {
  const root = panel(`<p data-i18n="status.loading"></p>`);
  void (async () => {
    try {
      const fragment = resolveSedrGuestFragment(inviteId);
      if (!fragment?.ps || !fragment.gw) {
        root.replaceWith(panel(`<p class="error-text" data-i18n="guest.invite.missingFragment"></p>`));
        return;
      }
      const { text, gate } = await guestApi.inviteGate(inviteId);
      const { secretKey } = deriveProofKeys(decodeBase64Url(fragment.ps));
      const signature = await signGateChallenge(secretKey, gate.nonce, text);
      const verified = await guestApi.inviteVerify(inviteId, signature);
      upsertPendingGuestInviteSeed({
        inviteId: verified.inviteId,
        roomId: verified.roomId,
        roomServerToken: verified.roomServerToken,
        envelopeBlobId: verified.envelopeBlobId,
        guestWrapKey: fragment.gw,
        displayName: verified.displayName,
        organization: verified.organization,
        email: verified.email,
        guestRole: verified.guestRole,
        allowCommenting: verified.allowCommenting,
      });

      try {
        const session = await guestApi.session();
        if (session.vaultSetupComplete) {
          if (!hasVaultSession()) {
            root.replaceWith(GuestUnlockForm(session));
            return;
          }
          const vault = loadVaultSession();
          if (!vault) {
            root.replaceWith(GuestUnlockForm(session));
            return;
          }
          const seed = listPendingGuestInviteSeeds().find((s) => s.inviteId === inviteId);
          if (seed) {
            await claimPendingInvite(seed, vault.userPrivateKey);
            removePendingGuestInviteSeed(inviteId);
          }
          window.location.assign('/guest/');
          return;
        }
      } catch {
        /* no account yet */
      }

      root.replaceWith(
        GuestSignupForm({
          email: verified.email,
          displayName: verified.displayName,
          organization: verified.organization,
        }),
      );
    } catch (cause) {
      console.error(cause);
      const code = (cause as { code?: string } | null)?.code;
      if (code === 'verify_locked_out' || code === 'rate_limited') {
        root.replaceWith(panel(`<p class="error-text">${apiErrorMessage(cause)}</p>`));
        return;
      }
      root.replaceWith(panel(`<p class="error-text" data-i18n="guest.invite.error"></p>
        <p class="muted">${t('guest.invite.errorLoginHint', { loginUrl: `${window.location.origin}/guest/` })}</p>`));
    }
  })();
  return root;
}

async function claimAllPendingInvites(userPrivateKey: Uint8Array): Promise<void> {
  const seeds = listPendingGuestInviteSeeds();
  if (seeds.length === 0) return;
  const failures: string[] = [];
  for (const seed of seeds) {
    try {
      await claimPendingInvite(seed, userPrivateKey);
      removePendingGuestInviteSeed(seed.inviteId);
    } catch (cause) {
      console.error('claim pending invite failed', seed.inviteId, cause);
      failures.push(seed.inviteId);
    }
  }
  if (failures.length > 0 && failures.length === seeds.length) {
    const error = new Error('claim_failed') as Error & { code?: string };
    error.code = 'claim_failed';
    throw error;
  }
}

async function claimPendingInvite(
  seed: PendingGuestInviteSeed,
  userPrivateKey: Uint8Array,
): Promise<void> {
  try {
    const envelopeBytes = await guestApi.fetchClaimEnvelope(seed.inviteId);
    const roomKey = await decryptSedrGuestEnvelope(
      envelopeBytes,
      decodeBase64Url(seed.guestWrapKey),
    );
    const userPublicKey = x25519.getPublicKey(userPrivateKey);
    const wrappedRoomKey = await wrapKey(
      roomKey,
      userPublicKey,
      `shareweb:sedr:member:${seed.roomId}`,
    );
    await guestApi.claimInvite(seed.inviteId, encodeBase64(wrappedRoomKey), seed.migrateTotp !== false);
  } catch (cause) {
    const code = (cause as { code?: string } | null)?.code;
    if (code === 'already_member' || code === 'invite_already_claimed') {
      return;
    }
    throw cause;
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
